import { User as UserIcon, Users, Wallet, Settings as SettingsIcon } from "lucide-react";

// User types
export interface CurrentUser {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  avatarURL: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  lastLoginAt: string | null;
  onboardingCompleted: boolean;
}

export interface Member {
  id: string;
  name?: string;
  email: string;
  role: string;
  avatarUrl?: string | null;
  invitationId?: string;
}

export interface PageInfo {
  startCursor?: string | null;
  endCursor?: string | null;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

export interface RoomBranding {
  logo_url?: string | null;
  brand_name?: string | null;
  accent_color?: string | null;
  redirect_url?: string | null;
  pre_join_bg_color?: string | null;
  welcome_message?: string | null;
}

export interface Organization {
  id: string;
  name: string;
  members?: Member[];
  settings?: {
    max_empty_timeout?: number;
    no_moderator_timeout?: number | null;
    billing_contact_name?: string | null;
    billing_contact_email?: string | null;
    branding?: RoomBranding | null;
  };
}

export interface UserOrganization {
  id: string;
  name: string;
  role: string;
  isPersonal?: boolean;
}

export interface CreditTransaction {
  id: string;
  createdAt: string;
  transactionType: 'credit' | 'debit';
  description?: string;
  sessionId?: string;
  amount: number;
  creditSource?: 'SUBSCRIPTION' | 'PREPAID' | 'BILLABLE';
}

export interface BillingInvoice {
  id: string;
  status: string;
  billingReason?: string | null;
  amountDueUsd: number;
  amountPaidUsd: number;
  currency: string;
  hostedInvoiceUrl?: string | null;
  invoicePdfUrl?: string | null;
  paymentMethodBrand?: string | null;
  paymentMethodLast4?: string | null;
  invoiceCreatedAt: string;
  finalizedAt?: string | null;
  paidAt?: string | null;
}

export interface BillingCapabilities {
  subscriptionCheckoutEnabled: boolean;
  subscriptionCheckoutUnavailableReason?: string | null;
  automaticBillingSetupEnabled: boolean;
  automaticBillingSetupUnavailableReason?: string | null;
  automaticBillingReady: boolean;
  prepaidTopupEnabled: boolean;
  prepaidTopupUnavailableReason?: string | null;
  billingPortalEnabled: boolean;
  billingPortalUnavailableReason?: string | null;
}

export type BillingProvider = 'MANUAL' | 'STRIPE';
export type SubscriptionPlan = 'PERSONAL' | 'PRO' | 'STUDIO' | 'ENTERPRISE';
export type SubscriptionStatus = 'ACTIVE' | 'CANCELLED' | 'EXPIRED';
export type SubscriptionChangeStatus = 'PENDING' | 'BLOCKED' | 'APPLIED' | 'CANCELLED' | 'FAILED';

export interface ScheduledSubscriptionChange {
  id?: string;
  sourcePlan: SubscriptionPlan;
  targetPlan: SubscriptionPlan;
  effectiveOn: string;
  status: SubscriptionChangeStatus;
  createdAt?: string;
  updatedAt?: string;
}

export interface Subscription {
  id: string;
  plan: SubscriptionPlan;
  status: SubscriptionStatus;
  billingProvider: BillingProvider;
  pendingBillingProvider?: BillingProvider | null;
  billingProviderSwitchAt?: string | null;
  monthlyCredits: number;
  rolloverCredits?: number;
  personalRooms: number;
  billingCycleStart?: string | null;
  billingCycleEnd?: string | null;
  priceUsd?: number;
  createdAt: string;
  cancelledAt?: string;
  expiresAt?: string;
  scheduledChange?: ScheduledSubscriptionChange | null;
}

export interface PlanConfig {
  plan: SubscriptionPlan;
  baseCredits: number;
  creditStep: number;
  pricePerStep: number;
  basePrice: number;
  basePersonalRooms: number;
  maxPersonalRooms: number;
  pricePerRoom: number;
  maxTeamMembers: number;
  hasConnectors: boolean;
}

export interface PlanChangePreview {
  currentPlan: SubscriptionPlan;
  targetPlan: SubscriptionPlan;
  creditsRemaining: number;
  rolloverCredits: number;
  usedThisCycle: number;
  targetMonthlyCredits: number;
  totalAfterUpgrade: number;
  prepaidCredits: number;
}

export interface CreditsBreakdown {
  balance: number;
  subscriptionCredits: number;
  prepaidCredits: number;
  billableCredits: number;
  billableAmountUsd: number;
  overageLockActive: boolean;
  monthlyAllocation: number;
  billingCycleEnd?: string;
  usedThisMonth: number;
  totalUsed: number;
}

export interface Session {
  id: string;
  createdAt?: string;
  date?: string;
  duration?: string;
}

// Settings sections configuration
export const userSettingsSections = [
  { id: "profile", label: "Profile", icon: UserIcon },
] as const;

export const orgSettingsSections = [
  { id: "credits", label: "Credits & Billing", icon: Wallet },
  { id: "members", label: "Members", icon: Users },
  { id: "room", label: "Room Settings", icon: SettingsIcon },
] as const;

export type SettingsSectionId = "profile" | "credits" | "members" | "room";

// Utility functions
export const getInitials = (nameOrEmail: string) => {
  if (!nameOrEmail) return "U";
  if (nameOrEmail.includes('@')) {
    return nameOrEmail[0].toUpperCase();
  }
  return nameOrEmail
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

export const formatDate = (dateString: string | null | undefined) => {
  if (!dateString) return "N/A";
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return dateString;
  }
};
